import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  calendarMonthGrid,
  dateInputDisplayToYmd,
  formatYmdMonthYear,
  istTodayYmd,
  maskDdMmYyyyInput,
  shiftYmdMonths,
  ymdStartOfMonth,
  ymdToDateInputDisplay,
} from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

import { Button } from '../actions/Button';
import { Input, type InputProps } from './Input';

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export interface IstDateFieldProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'defaultValue'> {
  /** Wire value: `yyyy-MM-dd` or empty. */
  value: string;
  /** Emits `yyyy-MM-dd` when a complete valid date is set, or `''` when cleared. */
  onChange: (ymd: string) => void;
}

/**
 * Date-only form control: **dd/mm/yyyy** with digits-only masking plus a calendar picker.
 * API/form wire value remains `yyyy-MM-dd`. Listings still use `formatDate`.
 */
export const IstDateField = forwardRef<HTMLInputElement, IstDateFieldProps>(function IstDateField(
  { value, onChange, onBlur, disabled, placeholder = 'dd/mm/yyyy', invalid, className, id, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const autoId = useId();
  const popoverId = `${id ?? autoId}-datepicker`;
  const wrapRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => ymdToDateInputDisplay(value));
  const [blurInvalid, setBlurInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => ymdStartOfMonth(value || istTodayYmd()));

  useEffect(() => {
    if (!focused) {
      setDraft(ymdToDateInputDisplay(value));
      setBlurInvalid(false);
    }
  }, [value, focused]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el || el.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const commitOrReject = (text: string): boolean => {
    const ymd = dateInputDisplayToYmd(text);
    if (ymd === null) {
      return false;
    }
    onChange(ymd);
    setDraft(ymd ? ymdToDateInputDisplay(ymd) : '');
    setBlurInvalid(false);
    return true;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = maskDdMmYyyyInput(e.target.value);
    setDraft(next);
    setBlurInvalid(false);

    if (next === '') {
      onChange('');
      return;
    }

    if (next.length === 10) {
      const ymd = dateInputDisplayToYmd(next);
      if (ymd) {
        onChange(ymd);
        setBlurInvalid(false);
        setVisibleMonth(ymdStartOfMonth(ymd));
      } else {
        setBlurInvalid(true);
      }
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    const text = draft.trim();
    if (!text) {
      onChange('');
      setDraft('');
      setBlurInvalid(false);
      onBlur?.(e);
      return;
    }
    const ok = commitOrReject(text);
    if (!ok) {
      setDraft(ymdToDateInputDisplay(value));
      setBlurInvalid(true);
    }
    onBlur?.(e);
  };

  const pickDate = (ymd: string) => {
    onChange(ymd);
    setDraft(ymdToDateInputDisplay(ymd));
    setBlurInvalid(false);
    setOpen(false);
  };

  const openPicker = () => {
    if (disabled) return;
    setVisibleMonth(ymdStartOfMonth(value || istTodayYmd()));
    setOpen((prev) => !prev);
  };

  const showInvalid = Boolean(invalid) || blurInvalid;
  const today = istTodayYmd();
  const days = useMemo(() => calendarMonthGrid(visibleMonth), [visibleMonth]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <Input
        ref={ref}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={10}
        invalid={showInvalid}
        pattern="\d{2}/\d{2}/\d{4}"
        title={placeholder}
        {...rest}
        value={focused ? draft : ymdToDateInputDisplay(value)}
        className={cn('pr-10', className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onFocus={() => {
          setFocused(true);
          setDraft(ymdToDateInputDisplay(value) || draft);
          setBlurInvalid(false);
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && (e.altKey || e.metaKey)) {
            e.preventDefault();
            openPicker();
            return;
          }
          if (e.ctrlKey || e.metaKey || e.altKey) return;
          const allowed =
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Enter' ||
            e.key === 'Escape' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'Home' ||
            e.key === 'End' ||
            e.key === '/' ||
            /^\d$/.test(e.key);
          if (!allowed) {
            e.preventDefault();
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          const pasted = e.clipboardData.getData('text') ?? '';
          const next = maskDdMmYyyyInput(pasted);
          setDraft(next);
          if (next === '') {
            onChange('');
            return;
          }
          if (next.length === 10) {
            const ymd = dateInputDisplayToYmd(next);
            if (ymd) {
              onChange(ymd);
              setBlurInvalid(false);
            } else {
              setBlurInvalid(true);
            }
          }
        }}
      />
      <button
        type="button"
        disabled={disabled}
        aria-label={t('ui.datePicker.open')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-sm text-fg-subtle',
          'hover:bg-surface-muted hover:text-fg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openPicker}
      >
        <CalendarDays className="h-4 w-4" aria-hidden />
      </button>

      {open ? (
        <div
          id={popoverId}
          role="dialog"
          aria-label={t('ui.datePicker.open')}
          className="absolute z-50 mt-1 w-[17.5rem] rounded-md border border-border bg-surface p-3 shadow-md"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label={t('ui.datePicker.prevMonth')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setVisibleMonth((m) => shiftYmdMonths(m, -1))}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <p className="min-w-[7rem] text-center text-sm font-semibold text-fg">
              {formatYmdMonthYear(visibleMonth)}
            </p>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label={t('ui.datePicker.nextMonth')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setVisibleMonth((m) => shiftYmdMonths(m, 1))}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {WEEKDAY_KEYS.map((key) => (
              <div
                key={key}
                className="px-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-fg-muted"
                aria-label={t(`ui.datePicker.weekdayLabels.${key}`)}
              >
                {t(`ui.datePicker.weekdays.${key}`)}
              </div>
            ))}
            {days.map((day) => {
              const selected = day.ymd === value;
              const isToday = day.ymd === today;
              return (
                <button
                  key={day.ymd}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickDate(day.ymd)}
                  className={cn(
                    'h-8 rounded-sm text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    day.inMonth ? 'text-fg' : 'text-fg-subtle',
                    selected
                      ? 'bg-primary text-fg-on-primary'
                      : 'hover:bg-surface-muted',
                    isToday && !selected && 'ring-1 ring-inset ring-primary',
                  )}
                >
                  {Number(day.ymd.slice(8, 10))}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickDate(today)}
            >
              {t('ui.datePicker.today')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
});
